"use client";

/* eslint-disable no-unused-vars */
import { useEffect, useState, useCallback } from "react";
import {
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
  MdFirstPage,
  MdLastPage,
} from "react-icons/md";
import Select from "react-select";
import { pageSizeDD } from "../../utilities/constants/constants";
import Button from "../buttons/Button";

export const PagingSerialNew = ({
  maxMenuHeight = 200,
  selected,
  setSelected,
  pageNo,
  setPageNo,
  numberOfPages,
  totalRecords = 0,
  showPageSizeSelector = true,
  showPageInfo = true,
  showFirstLast = true,
  maxPageLinks = 5,
  className = "",
  ...rest
}) => {
  const [inputPage, setInputPage] = useState("");

  // Memoized page change handler for better performance
  const onPageChangeHandler = useCallback(
    (page_no, action_type) => {
      let newPage = pageNo;

      switch (action_type) {
        case "First":
          newPage = 1;
          break;
        case "Last":
          newPage = numberOfPages;
          break;
        case "Next":
          newPage = Math.min(pageNo + 1, numberOfPages);
          break;
        case "Prev":
          newPage = Math.max(pageNo - 1, 1);
          break;
        case "Page":
          newPage = Math.max(1, Math.min(page_no, numberOfPages));
          break;
        default:
          return;
      }

      if (newPage !== pageNo) {
        setPageNo(newPage);
      }
    },
    [pageNo, numberOfPages, setPageNo],
  );

  // Enhanced page number generation with ellipsis
  const generatePageNumbers = useCallback(() => {
    const pageNumbers = [];

    if (numberOfPages <= maxPageLinks) {
      // Show all pages if total pages is less than max links
      for (let i = 1; i <= numberOfPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      const halfRange = Math.floor(maxPageLinks / 2);
      let startPage = Math.max(1, pageNo - halfRange);
      let endPage = Math.min(numberOfPages, pageNo + halfRange);

      // Adjust range if we're near the beginning or end
      if (endPage - startPage < maxPageLinks - 1) {
        if (startPage === 1) {
          endPage = Math.min(numberOfPages, startPage + maxPageLinks - 1);
        } else {
          startPage = Math.max(1, endPage - maxPageLinks + 1);
        }
      }

      // Add first page and ellipsis if needed
      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) {
          pageNumbers.push("...");
        }
      }

      // Add page range
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      // Add ellipsis and last page if needed
      if (endPage < numberOfPages) {
        if (endPage < numberOfPages - 1) {
          pageNumbers.push("...");
        }
        pageNumbers.push(numberOfPages);
      }
    }

    return pageNumbers;
  }, [pageNo, numberOfPages, maxPageLinks]);

  const pageNumbers = generatePageNumbers();

  // Handle direct page input
  const handlePageInput = (e) => {
    if (e.key === "Enter") {
      const page = Number.parseInt(inputPage);
      if (page && page >= 1 && page <= numberOfPages) {
        onPageChangeHandler(page, "Page");
        setInputPage("");
      }
    }
  };

  // Calculate current record range
  const currentPageSize = selected?.value || pageSizeDD[1]?.value || 10;
  const startRecord = (pageNo - 1) * currentPageSize + 1;
  const endRecord = Math.min(pageNo * currentPageSize, totalRecords);

  // Reset to page 1 when numberOfPages changes
  useEffect(() => {
    if (pageNo > numberOfPages && numberOfPages > 0) {
      setPageNo(1);
    }
  }, [numberOfPages, pageNo, setPageNo]);

  // Custom styles for react-select
  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: "40px",
      borderRadius: "8px",
      borderColor: state.isFocused ? "#3b82f6" : "#d1d5db",
      boxShadow: state.isFocused ? "0 0 0 2px rgba(59, 130, 246, 0.1)" : "none",
      "&:hover": {
        borderColor: "#3b82f6",
      },
      transition: "all 0.2s ease",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#3b82f6"
        : state.isFocused
          ? "#eff6ff"
          : "white",
      color: state.isSelected ? "white" : "#374151",
      "&:hover": {
        backgroundColor: state.isSelected ? "#3b82f6" : "#eff6ff",
      },
      transition: "all 0.2s ease",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 50,
      borderRadius: "8px",
      boxShadow:
        "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "#374151",
      fontWeight: "500",
    }),
  };

  // Don't render if no pages or only one page (unless showing page size selector)
  if (!numberOfPages || numberOfPages <= 1) {
    return showPageSizeSelector ? (
      <div
        className={`flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mt-6 p-4 bg-gray-50 rounded ${className}`}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Show:</span>
          <div className="min-w-[120px]">
            <Select
              maxMenuHeight={maxMenuHeight}
              options={pageSizeDD}
              value={selected || pageSizeDD[1]}
              isMulti={rest.isMulti}
              closeMenuOnSelect={rest.closeMenuOnSelect}
              onChange={setSelected}
              styles={selectStyles}
              isSearchable={false}
              {...rest}
            />
          </div>
          <span className="text-sm font-medium text-gray-700">entries</span>
        </div>
        {showPageInfo && totalRecords > 0 && (
          <div className="text-sm text-gray-600 bg-white px-3 py-2 rounded-md border">
            Showing {Math.min(totalRecords, currentPageSize)} of {totalRecords}{" "}
            entries
          </div>
        )}
      </div>
    ) : null;
  }

  return (
    <div
      className={`flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mt-8 
        bg-gradient-to-r from-gray-50 to-white rounded-lg ${className}`}
    >
      {/* Page Size Selector */}
      {showPageSizeSelector && (
        <div className="flex items-center gap-3 bg-white px-4 py-3 rounded border border-gray-200 shadow-sm">
          <span className="text-sm font-medium text-gray-700">Show:</span>
          <div className="min-w-[120px]">
            <Select
              maxMenuHeight={maxMenuHeight}
              options={pageSizeDD}
              value={selected || pageSizeDD[1]}
              isMulti={rest.isMulti}
              closeMenuOnSelect={rest.closeMenuOnSelect}
              onChange={setSelected}
              styles={selectStyles}
              isSearchable={false}
              {...rest}
            />
          </div>
          <span className="text-sm font-medium text-gray-700">entries</span>
        </div>
      )}

      {/* Pagination Controls */}
      <div className="flex flex-col xl:flex-row items-center gap-6">
        {/* Page Info */}
        {showPageInfo && (
          <div className="text-sm font-medium text-gray-600 bg-white px-4 py-2 rounded border border-gray-200 shadow-sm order-2 xl:order-1">
            {totalRecords > 0 ? (
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-primary rounded-full"></span>
                Showing{" "}
                <span className="font-bold text-primary">
                  {startRecord}
                </span> to{" "}
                <span className="font-bold text-primary">{endRecord}</span> of{" "}
                <span className="font-bold text-gray-800">{totalRecords}</span>{" "}
                entries
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-primary rounded-full"></span>
                Page <span className="font-bold text-primary">
                  {pageNo}
                </span> of{" "}
                <span className="font-bold text-gray-800">{numberOfPages}</span>
              </span>
            )}
          </div>
        )}

        {/* Pagination Buttons */}
        <div className="flex items-center gap-2 bg-white px-4 py-3 rounded border border-gray-200 shadow-sm order-1 xl:order-2">
          {/* First Page Button */}
          {showFirstLast && (
            <Button
              className={`p-2 rounded border transition-all duration-200 ${
                pageNo === 1
                  ? "cursor-not-allowed opacity-40 bg-gray-100 border-gray-200"
                  : "hover:bg-primary hover:text-white hover:border-primary hover:shadow-md border-gray-300 bg-white text-gray-600"
              }`}
              onClick={() => onPageChangeHandler(1, "First")}
              disabled={pageNo === 1}
              aria-label="Go to first page"
            >
              <MdFirstPage size={18} />
            </Button>
          )}

          {/* Previous Button */}
          <Button
            className={`px-3 py-2 rounded border transition-all duration-200 flex items-center gap-2 ${
              pageNo === 1
                ? "cursor-not-allowed opacity-40 bg-gray-100 border-gray-200 text-gray-400"
                : "hover:bg-primary hover:text-white hover:border-primary hover:shadow-md border-gray-300 bg-white text-gray-600"
            }`}
            onClick={() => onPageChangeHandler(pageNo, "Prev")}
            disabled={pageNo === 1}
            aria-label="Go to previous page"
          >
            <MdOutlineChevronLeft size={18} />
            <span className="hidden sm:inline text-sm font-medium">Prev</span>
          </Button>

          {/* Page Numbers */}
          <div className="flex items-center gap-1 mx-2">
            {pageNumbers.map((page, index) =>
              page === "..." ? (
                <span
                  key={`ellipsis-${index}`}
                  className="px-3 py-2 text-gray-400 font-medium"
                >
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => onPageChangeHandler(page, "Page")}
                  className={`min-w-[44px] px-3 py-2 text-sm font-medium rounded border transition-all duration-200 ${
                    page === pageNo
                      ? "bg-primary text-white border-primary shadow-md transform scale-105"
                      : "text-gray-700 border-gray-300 bg-white hover:bg-primary hover:text-white hover:border-primary hover:shadow-md"
                  }`}
                  aria-label={`Go to page ${page}`}
                  aria-current={page === pageNo ? "page" : undefined}
                >
                  {page}
                </button>
              ),
            )}
          </div>

          {/* Next Button */}
          <Button
            className={`px-3 py-2 rounded border transition-all duration-200 flex items-center gap-2 ${
              pageNo === numberOfPages
                ? "cursor-not-allowed opacity-40 bg-gray-100 border-gray-200 text-gray-400"
                : "hover:bg-primary hover:text-white hover:border-primary hover:shadow-md border-gray-300 bg-white text-gray-600"
            }`}
            onClick={() => onPageChangeHandler(pageNo, "Next")}
            disabled={pageNo === numberOfPages}
            aria-label="Go to next page"
          >
            <span className="hidden sm:inline text-sm font-medium">Next</span>
            <MdOutlineChevronRight size={18} />
          </Button>

          {/* Last Page Button */}
          {showFirstLast && (
            <Button
              className={`p-2 rounded border transition-all duration-200 ${
                pageNo === numberOfPages
                  ? "cursor-not-allowed opacity-40 bg-gray-100 border-gray-200"
                  : "hover:bg-primary hover:text-white hover:border-primary hover:shadow-md border-gray-300 bg-white text-gray-600"
              }`}
              onClick={() => onPageChangeHandler(numberOfPages, "Last")}
              disabled={pageNo === numberOfPages}
              aria-label="Go to last page"
            >
              <MdLastPage size={18} />
            </Button>
          )}
        </div>

        {/* Direct Page Input */}
        <div className="flex items-center gap-3 bg-white px-4 py-3 rounded border border-gray-200 shadow-sm order-3">
          <span className="text-sm font-medium text-gray-700">Go to:</span>
          <input
            type="number"
            min="1"
            max={numberOfPages}
            value={inputPage}
            onChange={(e) => setInputPage(e.target.value)}
            onKeyDown={handlePageInput}
            className="w-20 px-3 py-2 border border-gray-300 rounded text-center text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200"
            placeholder={pageNo.toString()}
            aria-label="Go to page number"
          />
          <span className="text-sm text-gray-500">of {numberOfPages}</span>
        </div>
      </div>
    </div>
  );
};
